// Our projects — the website showcase of past installations (Jacky, 25 October 2026: "remove the commercial replace it
// with page showcase Artemis Flooring, past project that read from installation photo, dont list specific but larger
// date period"). One set of rules for every place that needs them: the showcase commands (packages/commands), the
// API that renders the public copy of a photo, the CRM Showcase page and the website's /projects page.
//
// Anonymous by construction (Jacky: "photo stay anonoymous, no customer info is to be revealed"): a released project
// shows a title, a summary, a season and its floor types — never a name, address, suburb or Job reference. The
// summary is checked against the Job's own customer details before it can be released (showcaseLeaks).
import { z } from "zod";
/** The website's floor types (apps/web catalogue SUBTYPES), the only floor-type filter the page offers. */
export const SHOWCASE_FLOOR_TYPES = ["Carpet", "Carpet Tile", "Engineered Timber", "Hybrid", "Laminate", "Solid Timber", "Vinyl Plank", "Vinyl Sheet"];
export const SHOWCASE_SEASONS = ["summer", "autumn", "winter", "spring"];
export const SHOWCASE_WATERMARKS = ["bottom-right", "bottom-left", "top-right", "top-left", "none"];
export const SHOWCASE_STATUSES = ["draft", "released", "taken_down"];
/** Where a showcase photo's private original lives. Legacy installation photos are in the old platform's private
 *  `artemis-app` container; crew photos from the AIW installer app are in `aiw-uploads`. Neither is ever served. */
export const SHOWCASE_SOURCES = ["installation", "installer_evidence"];
export const SHOWCASE_SOURCE_PATH = {
    installation: /^installs\/[A-Za-z0-9_-]{1,80}\/[A-Za-z0-9_.\/-]{1,300}\.(jpe?g|png|webp)$/i,
    installer_evidence: /^installer-evidence\/v1\/[a-f0-9]{64}\/[a-f0-9-]{36}\.(jpg|png|webp)$/,
};
/** EmmA's AI retouch output (the render rig writes it into `aiw-uploads`), awaiting Jacky's click. */
export const SHOWCASE_RETOUCH_PATH = /^showcase-retouch\/[0-9a-f-]{36}\/[0-9a-f-]{36}\.(png|jpg|webp)$/;
/** The touch-ups a person or EmmA makes. Instructions, never pixels: the original is never changed, and the public
 *  copy is rendered again from it whenever the recipe changes. Every field has a neutral default. */
export const ShowcaseRecipe = z.object({
    /** Quarter turns, clockwise, after the camera's own orientation is applied. */
    rotate: z.union([z.literal(0), z.literal(90), z.literal(180), z.literal(270)]).default(0),
    /** Fine straightening in degrees; the edges the turn exposes are cropped away. */
    straighten: z.number().min(-10).max(10).multipleOf(0.5).default(0),
    /** Cropped off each edge, as a fraction of that side (after rotation). */
    trim: z.object({
        top: z.number().min(0).max(0.4).default(0), right: z.number().min(0).max(0.4).default(0),
        bottom: z.number().min(0).max(0.4).default(0), left: z.number().min(0).max(0.4).default(0),
    }).default({ top: 0, right: 0, bottom: 0, left: 0 }),
    brightness: z.number().min(-0.5).max(0.5).default(0),
    contrast: z.number().min(-0.5).max(0.5).default(0),
    /** Gentle clean-up of phone noise: 0 off, 3 strongest. The AI retouch is separate (retouch_*). */
    smooth: z.number().int().min(0).max(3).default(0),
    watermark: z.enum(SHOWCASE_WATERMARKS).default("bottom-right"),
}).strict();
export const DEFAULT_SHOWCASE_RECIPE = ShowcaseRecipe.parse({});
/** A stable short fingerprint (cyrb53) — pure JS so the contract stays importable in a browser. */
function fingerprint(text) {
    let h1 = 0xdeadbeef, h2 = 0x41c6ce57;
    for (let i = 0; i < text.length; i++) {
        const ch = text.charCodeAt(i);
        h1 = Math.imul(h1 ^ ch, 2654435761);
        h2 = Math.imul(h2 ^ ch, 1597334677);
    }
    h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
    h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
    return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36).padStart(11, "0");
}
const canonical = (r) => JSON.stringify([r.rotate, r.straighten, r.trim.top, r.trim.right, r.trim.bottom, r.trim.left, r.brightness, r.contrast, r.smooth, r.watermark]);
/** The recipe's pixels, without the watermark: what EmmA's retouch was made from. A retouch whose `made_from` no
 *  longer matches has gone stale (the photo was turned or cropped since) and is not used. */
export function showcaseBaseVersion(recipe) {
    return fingerprint(canonical({ ...recipe, watermark: "none" }));
}
/** The public copy's version: the source, the recipe and an accepted retouch. It is part of the image URL, so a
 *  changed photo is a new URL and no cache can show the old one. */
export function showcaseVersion(sourcePath, recipe, retouchPath) {
    return fingerprint(`${RENDER_REVISION}|${sourcePath}|${canonical(recipe)}|${retouchPath ?? ""}`);
}
/** Bump when the renderer itself changes how pixels come out, so every public copy is rendered again. */
export const RENDER_REVISION = 1;
/** Sydney seasons (southern hemisphere): Dec–Feb summer, Mar–May autumn, Jun–Aug winter, Sep–Nov spring. December
 *  belongs to the summer that starts in it, so 15 Dec 2025 is "Summer 2025–26" with year 2025. */
export function showcaseSeasonOf(day) {
    const m = /^(\d{4})-(\d{2})-\d{2}$/.exec(day);
    if (!m)
        throw new Error(`not a day: ${day}`);
    const year = Number(m[1]), month = Number(m[2]);
    if (month === 12)
        return { season: "summer", year };
    if (month <= 2)
        return { season: "summer", year: year - 1 };
    return { season: month <= 5 ? "autumn" : month <= 8 ? "winter" : "spring", year };
}
export function showcasePeriodLabel(season, year) {
    const name = season[0].toUpperCase() + season.slice(1);
    return season === "summer" ? `${name} ${year}–${String((year + 1) % 100).padStart(2, "0")}` : `${name} ${year}`;
}
/** Newest first: the page lists periods in this order. */
export const showcasePeriodOrder = (season, year) => year * 4 + ["summer", "autumn", "winter", "spring"].indexOf(season);
/** The floor types a Job's accepted quote lays, from its main-product lines (quote payload tier_1_lines). */
// A floor's colour name for the showcase (26 Oct 2026). The catalogue's colour when it has one; otherwise the variant's
// name with a leading "<product> - " taken off, because many variants are named "Mulprova 12mm - Blackbutt". A variant
// whose name only repeats the product (single-colour products such as Wonderwood European Oak Natural 14mm) has no
// colour to show: null, never the product name a second time.
// 29 Oct 2026: "TBC" is a placeholder, not a colour. Every range carries a TBC variant (56 in the catalogue) for a sale
// made before the colour is chosen, and many Jobs are ordered or quoted on it; read as a colour, the website would say
// "Stonegrove Luxury Brevis · TBC". So it counts as no colour, and the quote's real colour (Rime) can fill it.
const PLACEHOLDER_COLOUR = /^(tbc|tba|tbd|to be (confirmed|advised|decided))$/i;
export function showcaseColourName(product, colour, variantName) {
    const norm = (s) => s.trim().toLowerCase();
    const real = (s) => !!s && norm(s) !== norm(product) && !PLACEHOLDER_COLOUR.test(s.trim());
    const given = (colour ?? "").trim();
    if (real(given))
        return given;
    let name = (variantName ?? "").trim();
    const prefix = product.trim();
    if (prefix && norm(name).startsWith(norm(prefix)))
        name = name.slice(prefix.length).replace(/^[\s\-–—:·|]+/, "").trim();
    return real(name) ? name : null;
}
export function showcaseFloorTypes(lines) {
    const out = new Set();
    for (const raw of Array.isArray(lines) ? lines : []) {
        const l = (raw && typeof raw === "object" ? raw : {});
        if (l.category !== "Main Product")
            continue;
        const type = String(l.type ?? "").toLowerCase(), sub = String(l.sub_type ?? "").toLowerCase();
        if (sub.includes("tile") && (type === "carpet" || sub.includes("carpet")))
            out.add("Carpet Tile");
        else if (type === "carpet")
            out.add("Carpet");
        else if (sub.includes("sheet"))
            out.add("Vinyl Sheet");
        else if (sub.includes("vinyl plank") || (type === "vinyl" && sub.includes("plank")))
            out.add("Vinyl Plank");
        else if (sub.includes("engineered"))
            out.add("Engineered Timber");
        else if (sub.includes("solid"))
            out.add("Solid Timber");
        else if (sub.includes("hybrid"))
            out.add("Hybrid");
        else if (sub.includes("laminate"))
            out.add("Laminate");
    }
    return SHOWCASE_FLOOR_TYPES.filter((t) => out.has(t));
}
const norm = (s) => s.toLowerCase().normalize("NFKD").replace(/[̀-ͯ]/g, "").replace(/[^a-z0-9]+/g, " ").trim();
const STREET_TYPES = /\b(street|st|road|rd|avenue|ave|av|drive|dr|place|pl|parade|pde|crescent|cres|cr|close|cl|court|ct|lane|ln|way|terrace|tce|highway|hwy|boulevard|blvd|circuit|cct|grove|gr|square|sq)\b/g;
/** Words a name part can share with ordinary writing; on their own they are not treated as a name. */
const COMMON = new Set(["the", "and", "mr", "mrs", "ms", "dr", "pty", "ltd", "group", "home", "house", "unit", "level", "floor", "floors", "carpet", "timber", "hybrid", "vinyl", "new", "old", "rose", "may", "joy", "hope", "grace", "bay", "park", "hill", "hills", "north", "south", "east", "west", "strata", "builder", "builders", "construction", "property", "properties", "real", "estate", "trust"]);
const hasWord = (text, phrase) => phrase.length > 0 && new RegExp(`(?:^| )${phrase.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(?: |$)`).test(text);
/** Every customer detail found in the text a project will publish. Empty means it may be released. Also refuses any
 *  e-mail address, phone number or Job-style reference at all, whoever's it is. */
export function showcaseLeaks(text, ids) {
    const t = norm(text), out = [];
    const add = (kind, found) => { if (!out.some((l) => l.kind === kind && l.found === found))
        out.push({ kind, found }); };
    for (const name of ids.names) {
        const full = norm(name);
        if (full.length >= 3 && hasWord(t, full))
            add("name", name.trim());
        for (const part of full.split(" "))
            if (part.length >= 3 && !COMMON.has(part) && !/^\d+$/.test(part) && hasWord(t, part))
                add("name", part);
    }
    for (const street of ids.streets) {
        const s = norm(street).replace(STREET_TYPES, " ").replace(/\b\d+[a-z]?\b/g, " ").replace(/\s+/g, " ").trim();
        if (s.length >= 3 && !COMMON.has(s) && hasWord(t, s))
            add("street", street.trim());
    }
    for (const suburb of ids.suburbs) {
        const s = norm(suburb);
        if (s.length >= 3 && hasWord(t, s))
            add("suburb", suburb.trim());
    }
    for (const ref of ids.refs) {
        const r = norm(ref);
        if (r && hasWord(t, r))
            add("reference", ref);
    }
    const anyRef = /\b(?:[JT]\d{9}|[PJ]-\d{4}-\d{5}|O-\d{8})\b/i.exec(text);
    if (anyRef)
        add("reference", anyRef[0]);
    for (const e of ids.emails)
        if (e && text.toLowerCase().includes(e.toLowerCase()))
            add("email", e);
    const anyEmail = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/.exec(text);
    if (anyEmail)
        add("email", anyEmail[0]);
    const packed = text.replace(/[\s().-]/g, "");
    for (const p of ids.phones) {
        const d = p.replace(/\D/g, "").slice(-8);
        if (d.length === 8 && packed.includes(d))
            add("phone", p);
    }
    const anyPhone = /(?:\+61|0)[2-478]\d{8}/.exec(packed);
    if (anyPhone)
        add("phone", anyPhone[0]);
    return out;
}
/** A floor an entry shows, as stored: catalogue ids only (26 Oct 2026). The names come from the catalogue when published. */
export const ShowcaseFloor = z.object({ product_id: z.string().regex(/^\d{1,20}$/), variant_id: z.string().regex(/^\d{1,20}$/).nullable() }).strict();
/** The same floor as the website shows it: catalogue facts only — the product, its colour and its floor type. */
export const ShowcasePublicFloor = z.object({ product_id: z.string(), product: z.string(), colour: z.string().nullable(), floor_type: z.enum(SHOWCASE_FLOOR_TYPES).nullable() }).strict();
/** What one released project publishes. Nothing else about a project ever leaves the backbone. */
export const ShowcasePublicProject = z.object({
    id: z.string().uuid(),
    title: z.string(),
    summary: z.string(),
    season: z.enum(SHOWCASE_SEASONS),
    year: z.number().int(),
    floor_types: z.array(z.enum(SHOWCASE_FLOOR_TYPES)),
    /** Catalogue facts, never customer data. Defaults empty so an older API answer still parses. */
    floors: z.array(ShowcasePublicFloor).default([]),
    photos: z.array(z.object({ id: z.string().uuid(), v: z.string().regex(/^[0-9a-z]{11}$/) })),
}).strict();
//# sourceMappingURL=showcase.js.map